<template>
  <div class="background-process-tray">
    <Button
      class="bp-trigger"
      severity="secondary"
      @click="togglePanel"
      v-tooltip.bottom="'Procesos en segundo plano'"
      rounded
    >
      <i class="pi pi-cloud-upload"></i>
    </Button>
    <span v-if="activeCount > 0" class="bp-badge">{{ activeCount }}</span>

    <OverlayPanel
      ref="panel"
      class="bp-panel"
      appendTo="body"
      :showCloseIcon="false"
      @hide="onHide"
      @show="onShow"
    >
      <div class="flex flex items-center justify-content-between px-3">
        <div>
          <h4 class="font-semibold">Procesos en segundo plano</h4>
          <div>
            <small class="text-gray-600"
              >{{ activeCount }} proceso(s) en curso</small
            >
          </div>
        </div>
        <Button
          class="text-gray-600 hover:text-gray-800 align-self-start"
          icon="pi pi-refresh"
          severity="secondary"
          text
          rounded
          :loading="state.loadingInitial"
          @click="refresh"
        />
      </div>
      <Divider />

      <div class="bp-list" ref="listContainer" @scroll.passive="handleScroll">
        <div v-if="state.loadingInitial" class="bp-empty">
          <i class="pi pi-spin pi-spinner"></i>
          <span>Cargando procesos...</span>
        </div>

        <div v-else-if="processes.length === 0" class="bp-empty">
          <i class="pi pi-info-circle"></i>
          <span>No hay procesos recientes.</span>
        </div>

        <template v-else>
          <article
            v-for="process in processes"
            :key="process.id"
            class="bp-item"
          >
            <header class="bp-item-header">
              <div>
                <span class="bp-item-title font-medium">
                  {{ typeLabel(process.type) }}
                </span>
                <span class="bp-item-meta">
                  <span class="bp-status" :class="statusClass(process.status)">
                    {{ statusLabel(process.status) }} </span
                  >• hace
                  {{ fromTime(new Date(process.createdAt)) }}
                </span>
              </div>
              <div
                class="text-medium align-self-center"
                v-if="!isTerminal(process.status)"
              >
                <i class="pi pi-spin pi-spinner ml-2 mr-3"></i>
              </div>
              <div class="" v-else>
                <template v-if="process.error">
                  <i class="pi pi-exclamation-triangle text-red-500 mr-2"></i>
                  <span>{{ process.error }}</span>
                </template>
                <template v-else-if="process.result">
                  <Button
                    class="bp-result-link"
                    text
                    severity="primary"
                    label=""
                    :loading="isResultDownloading(process.id)"
                    :icon="
                      isResultDownloading(process.id)
                        ? undefined
                        : 'pi pi-download'
                    "
                    @click="downloadResult(process)"
                  />
                </template>
                <template v-else>
                  <span class="text-sm"
                    >Finalizado el {{ formatDate(process.updatedAt) }}</span
                  >
                </template>
              </div>
            </header>
          </article>

          <div class="bp-loader" v-if="state.loadingMore">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
          <div v-else-if="!hasMore" class="bp-end">
            <span>Has llegado al final.</span>
          </div>
        </template>
      </div>
    </OverlayPanel>
  </div>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs'
  import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
  import type { Subscription } from 'rxjs'
  import type OverlayPanel from 'primevue/overlaypanel'
  import PaginatedResult from '@/shared/dtos/PaginatedResult'
  import { BackgroundProcess } from '@/modules/hr/services/interfaces'
  import {
    BACKGROUND_PROCESS_EVENTS_KEY,
    listBackgroundProcesses,
  } from '@/modules/hr/services/backgroundProcessService'
  import { ObservableService } from '@/shared/services'
  import { getFileUrlPresigned } from '@/shared/services/fileService'
  import { useToast } from 'primevue/usetoast'
  import fromTime from '../utils/fromTime'

  const DEFAULT_LIMIT = 10
  const POLLING_INTERVAL = 8000
  const TERMINAL_STATUSES = ['COMPLETED', 'FAILED', 'CANCELED']

  const panel = ref<OverlayPanel | null>(null)
  const listContainer = ref<HTMLElement | null>(null)
  const processes = ref<BackgroundProcess[]>([])
  const hasLoadedOnce = ref(false)
  const loadingSilently = ref(false)

  const state = reactive<{
    meta: PaginatedResult<BackgroundProcess>['meta'] | null
    loadingInitial: boolean
    loadingMore: boolean
  }>({
    meta: null,
    loadingInitial: false,
    loadingMore: false,
  })

  const observableService = ObservableService.getInstance()
  const toast = useToast()
  let refreshSubscription: Subscription | undefined
  let pollingTimeout: ReturnType<typeof setTimeout> | undefined
  const downloadingResults = reactive<Record<string, boolean>>({})

  const activeCount = computed(
    () =>
      processes.value.filter((process) => !isTerminal(process.status)).length
  )

  const hasMore = computed(() => {
    if (!state.meta) return false
    const { currentPage = 0, lastPage = 0 } = state.meta
    return currentPage < lastPage
  })

  const isTerminal = (status: string) => TERMINAL_STATUSES.includes(status)

  const statusLabel = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'Pendiente'
      case 'RUNNING':
        return 'En progreso'
      case 'COMPLETED':
        return 'Completado'
      case 'FAILED':
        return 'Fallido'
      case 'CANCELED':
        return 'Cancelado'
      default:
        return status
    }
  }

  const statusClass = (status: string) => {
    if (status === 'FAILED' || status === 'CANCELED') return 'bp-status--error'
    if (status === 'COMPLETED') return 'bp-status--success'
    if (status === 'RUNNING') return 'bp-status--running'
    return 'bp-status--pending'
  }

  const typeLabel = (type: string) => {
    switch (type) {
      case 'BulkPayslipsRequested':
        return 'Recibos masivos'
      case 'BulkLettersRequested':
        return 'Cartas masivas'
      default:
        return type
    }
  }

  const formatDate = (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm')

  const extractResultKey = (result?: string | null): string | null => {
    if (!result) return null
    try {
      const parsed = JSON.parse(result)
      if (parsed?.key) return parsed.key
      if (parsed?.data?.key) return parsed.data.key
      if (parsed?.result?.key) return parsed.result.key
    } catch (error) {
      // not JSON, treat as raw key
    }
    return result
  }

  const isResultDownloading = (id: string) => Boolean(downloadingResults[id])

  const downloadResult = async (process: BackgroundProcess) => {
    const key = extractResultKey(process.result)
    if (!key || downloadingResults[process.id]) {
      return
    }

    downloadingResults[process.id] = true

    try {
      const presignedUrl = await getFileUrlPresigned(
        key,
        process.type === 'BulkPayslipsRequested'
          ? 'massivepayslipsstack-documentsbucket9ec9deb9-mjeygktg6zyw'
          : 'massivelettersstack-documentsbucket9ec9deb9-4i2bl3ort5hi'
      )
      const link = document.createElement('a')
      link.href = presignedUrl
      link.download = `${key.split('/').pop()}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(presignedUrl)
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: 'Descarga fallida',
        detail: 'No pudimos obtener el enlace del archivo',
        life: 4000,
      })
    } finally {
      delete downloadingResults[process.id]
    }
  }

  const mergeProcesses = (newItems: BackgroundProcess[], reset: boolean) => {
    if (reset) {
      processes.value = [...newItems]
    } else {
      const map = new Map(processes.value.map((item) => [item.id, item]))
      newItems.forEach((item) => {
        const existing = map.get(item.id)
        map.set(item.id, { ...existing, ...item })
      })
      processes.value = Array.from(map.values())
    }

    processes.value.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }

  const stopPolling = () => {
    if (pollingTimeout) {
      clearTimeout(pollingTimeout)
      pollingTimeout = undefined
    }
  }

  const schedulePolling = () => {
    stopPolling()
    const hasActive = processes.value.some((item) => !isTerminal(item.status))
    if (!hasActive) {
      return
    }

    pollingTimeout = setTimeout(() => {
      void loadProcesses({ reset: true, silent: true })
    }, POLLING_INTERVAL)
  }

  const loadProcesses = async ({
    reset = false,
    offset,
    silent = false,
  }: {
    reset?: boolean
    offset?: number
    silent?: boolean
  } = {}) => {
    if (silent) {
      if (loadingSilently.value) return
    } else if (reset) {
      if (state.loadingInitial) return
    } else {
      if (state.loadingMore || !hasMore.value) return
    }

    if (silent) {
      loadingSilently.value = true
    } else if (reset) {
      state.loadingInitial = true
    } else {
      state.loadingMore = true
    }

    try {
      const limit = state.meta?.perPage ?? DEFAULT_LIMIT
      const currentPage = state.meta?.currentPage ?? 0
      const targetPage =
        offset !== undefined ? offset : reset ? 1 : currentPage + 1

      const response = await listBackgroundProcesses({
        limit,
        offset: targetPage,
      })

      mergeProcesses(response.data, reset)
      state.meta = response.meta
    } finally {
      if (silent) {
        loadingSilently.value = false
      } else {
        state.loadingInitial = false
        state.loadingMore = false
      }
      hasLoadedOnce.value = true
      if (reset) {
        schedulePolling()
      }
    }
  }

  const handleScroll = async () => {
    if (!listContainer.value) return
    const el = listContainer.value

    const threshold = 80
    if (
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold &&
      !state.loadingMore &&
      hasMore.value
    ) {
      await loadProcesses({ reset: false })
    }
  }

  const togglePanel = (event: MouseEvent) => {
    panel.value?.toggle(event)
  }

  const refresh = async () => {
    await loadProcesses({ reset: true })
  }

  const onShow = async () => {
    if (!hasLoadedOnce.value) {
      await loadProcesses({ reset: true })
    }
  }

  const onHide = () => {
    // noop for now, keep subscriptions running
  }

  onMounted(async () => {
    if (!observableService.getObservable(BACKGROUND_PROCESS_EVENTS_KEY)) {
      observableService.createObservable(BACKGROUND_PROCESS_EVENTS_KEY)
    }

    refreshSubscription = observableService
      .getObservable(BACKGROUND_PROCESS_EVENTS_KEY)
      ?.subscribe((event) => {
        if (!event) return
        if (event.action === 'created' || event === 'refresh') {
          void loadProcesses({ reset: true })
        }
      })

    await loadProcesses({ reset: true })
  })

  onBeforeUnmount(() => {
    refreshSubscription?.unsubscribe()
    stopPolling()
  })
</script>

<style scoped>
  .background-process-tray {
    position: relative;
    margin-right: 1rem;
  }

  .bp-trigger {
    position: relative;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .bp-trigger i {
    font-size: 1.1rem;
  }

  .bp-badge {
    position: absolute;
    top: -0.25rem;
    right: -0.25rem;
    background-color: #6aa5ff;
    color: rgb(235, 235, 235);
    border-radius: 999px;
    padding: 0.1rem 0.4rem;
    font-size: 0.75rem;
    font-weight: 400;
  }

  :deep(.bp-panel) {
    padding: 0;
  }

  .bp-panel {
    width: 420px;
    max-width: 90vw;
  }

  .bp-refresh {
    width: 2.5rem;
    height: 2.5rem;
  }

  .bp-list {
    max-height: 420px;
    overflow-y: auto;
    padding: 0 1.25rem 1rem;
  }

  .bp-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    gap: 0.75rem;
    padding: 2rem 1rem;
    text-align: center;
  }

  .bp-empty i {
    font-size: 1.5rem;
  }

  .bp-item {
    padding: 1rem 0;
    border-bottom: 1px solid #e5e7eb;
  }

  .bp-item:last-of-type {
    border-bottom: none;
  }

  .bp-item-header {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
  }

  .bp-item-meta {
    display: block;
    font-size: 0.8rem;
    color: #6b7280;
    margin-top: 0.1rem;
  }

  .bp-status {
    align-self: flex-start;
    font-size: 0.6rem;
    font-weight: 600;
    padding: 0.25rem 0.5rem;
    border-radius: 999px;
    text-transform: uppercase;
  }

  .bp-status--pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  .bp-status--running {
    background-color: #dbeafe;
    color: #1d4ed8;
  }

  .bp-status--success {
    background-color: #dcfce7;
    color: #166534;
  }

  .bp-status--error {
    background-color: #fee2e2;
    color: #b91c1c;
  }

  .bp-result {
    margin-top: 0.75rem;
    font-size: 0.85rem;
    color: #374151;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :deep(.p-button-label) {
    font-weight: 400;
  }

  .bp-loader,
  .bp-end {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem 0;
    color: #6b7280;
    font-size: 0.85rem;
  }

  .bp-end {
    font-style: italic;
  }

  @media (max-width: 520px) {
    .bp-panel {
      width: 90vw;
    }
  }
</style>
